import React, { useState } from 'react';
import { Loader2, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { appendFiles } from '../../utils/api';
import { useT } from '../../i18n';
import SecondFileDropzone from './SecondFileDropzone';
import MissingChoice from './MissingChoice';

/**
 * Stack a second file under the current one.
 *
 * The vertical counterpart of Merge, which joins side by side on a key. What
 * the user has to decide is what to do with columns only one of the two files
 * carries — and the answer is shown before applying, not discovered after.
 */
const AppendOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [secondFile, setSecondFile] = useState(null);
  const [secondInfo, setSecondInfo] = useState(null);
  const [keep, setKeep] = useState('common');
  const [applying, setApplying] = useState(false);

  const handleUploaded = (result, file) => {
    setSecondFile(file);
    setSecondInfo(result.info);
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const result = await appendFiles({ keep });
      (result.warnings || []).forEach((warning) => toast.warning(warning, { duration: 8000 }));
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('append.failed'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 text-muted-foreground">
        <Layers className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <p className="text-sm">{t('append.intro')}</p>
      </div>

      {!secondFile ? (
        <SecondFileDropzone label={t('append.uploadSecond')} onUploaded={handleUploaded} />
      ) : (
        <>
          <div className="card p-4 bg-emerald-50 border-emerald-200">
            <p className="text-sm font-medium text-emerald-800">{secondFile.name}</p>
            <p className="text-xs text-emerald-700 mt-1">
              {t('append.secondSummary', {
                rows: secondInfo?.rows?.toLocaleString(),
                columns: secondInfo?.columns,
              })}
            </p>
          </div>

          <div>
            <label className="label mb-2 block">{t('append.columnsQuestion')}</label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  checked={keep === 'common'}
                  onChange={() => setKeep('common')}
                  className="mt-1"
                  data-testid="append-keep-common"
                />
                <div>
                  <span className="text-sm font-medium">{t('append.keepCommon')}</span>
                  <p className="text-xs text-muted-foreground">{t('append.keepCommonHelp')}</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  checked={keep === 'all'}
                  onChange={() => setKeep('all')}
                  className="mt-1"
                  data-testid="append-keep-all"
                />
                <div>
                  <span className="text-sm font-medium">{t('append.keepAll')}</span>
                  <p className="text-xs text-muted-foreground">{t('append.keepAllHelp')}</p>
                </div>
              </label>
            </div>
          </div>
        </>
      )}

      <MissingChoice reasons={[!secondFile && t('form.needSecondFile')]} />

      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <button onClick={onCancel} className="btn-secondary" data-testid="append-cancel">
          {t('form.cancel')}
        </button>
        <button
          onClick={handleApply}
          disabled={applying || !secondFile}
          className="btn-primary flex items-center gap-2"
          data-testid="append-apply"
        >
          {applying && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('append.apply')}
        </button>
      </div>
    </div>
  );
};

export default AppendOperation;
